/**
 * Shared page primitives, mirroring the partner mock's `components/ui.tsx`.
 *
 * WHY THIS FILE EXISTS: every page here hand-rolled its own `<h2>` + `<p>` header and its own
 * row of tab buttons, so no two pages agreed on spacing, weight, container width or tab styling.
 * That is the whole reason our layout drifted from the mock. Pages should compose these instead
 * of re-inventing them — the mock's API is kept deliberately (`title`/`subtitle`,
 * `tabs=[{key,label,count}]`/`active`/`onChange`) so its screens port across with no translation.
 *
 * Uses our theme tokens (`theme-text-primary`, `theme-border-primary`, …) rather than the mock's
 * raw palette names, so light/dark keeps working.
 */
import { useEffect, useId, useLayoutEffect, useRef, useState, type ComponentType, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { relTime } from '../utils/date'

/**
 * Two content widths.
 *
 * `wide` is the reading column every settings LIST uses — `mx-auto max-w-5xl`, as in the mock.
 * `full` gives the whole content area to a wizard FORM. The mock sized its wizards as modals
 * (`Modal size="wide" | "xl"`), but ours live in the content area rather than over it, so a modal's
 * width cap is the wrong constraint: it left a two-column form with a live preview squeezed into
 * 64rem while the page had room to spare.
 */
export type PageWidth = 'wide' | 'full'

/** Centred content column. The mock wraps every settings page in `mx-auto max-w-5xl`. */
export function PageShell({ children, size = 'wide' }: {
  children: ReactNode
  size?: PageWidth
}) {
  return <div className={`mx-auto ${size === 'full' ? 'max-w-none' : 'max-w-5xl'}`}>{children}</div>
}

export function PageHeader({ title, subtitle, actions }: {
  title: string
  subtitle?: ReactNode
  actions?: ReactNode
}) {
  return (
    <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-ftext">{title}</h1>
        {subtitle && (
          <p className="mt-1 max-w-3xl text-sm text-fmuted">{subtitle}</p>
        )}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  )
}

export interface TabSpec {
  key: string
  label: string
  /** Rendered as a pill beside the label. `undefined` shows nothing — 0 is a real count. */
  count?: number
}

export function Tabs({ tabs, active, onChange }: {
  tabs: TabSpec[]
  active: string
  onChange: (key: string) => void
}) {
  // Underlined tabs sitting on a rule, as the mock draws them (its ui.tsx:210). The selected tab
  // is marked by the accent rule under it, so the row reads as one control rather than a set of
  // buttons.
  return (
    <div className="flex gap-1 border-b border-theme-border-primary" role="tablist">
      {tabs.map((t) => {
        const on = t.key === active
        return (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={on}
            onClick={() => onChange(t.key)}
            className={`relative -mb-px flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-semibold transition-colors ${
              on
                ? 'border-theme-accent-blue text-theme-text-primary'
                : 'border-transparent text-theme-text-secondary hover:text-theme-text-primary'
            }`}
          >
            {t.label}
            {/* `!= null` on purpose: 0 is a meaningful count and must still render. */}
            {t.count != null && (
              <span
                className={`rounded-full px-1.5 py-0.5 text-[11px] ${
                  on
                    ? 'bg-theme-accent-blue/20 text-theme-accent-blue'
                    : 'bg-theme-tertiary text-theme-text-secondary'
                }`}
              >
                {t.count}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}

/**
 * Collapsed-by-default explainer, as the mock's `HowItWorks`: the explanation is there if you
 * want it and invisible if you don't, so it never pushes the actual content below the fold.
 */
export function HowItWorks({ title = 'How it works', children }: {
  title?: string
  children: ReactNode
}) {
  const [open, setOpen] = useState(false)
  return (
    <div className="mb-5">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 text-[13px] font-medium text-theme-accent-blue hover:underline"
      >
        <svg
          className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`}
          viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"
        >
          <path fillRule="evenodd" clipRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" />
        </svg>
        {title}
      </button>
      {open && (
        <div className="mt-3 space-y-2 rounded-lg border border-theme-border-primary bg-theme-secondary p-4 text-sm text-theme-text-secondary">
          {children}
        </div>
      )}
    </div>
  )
}

/** Breadcrumb. A full-page wizard has no dismiss affordance of its own, so this is the exit. */
export function Breadcrumb({ trail }: {
  /** Last entry is the current location and is rendered as plain text, not a link. */
  trail: { label: string; onClick?: () => void }[]
}) {
  return (
    <nav className="mb-4 flex items-center gap-2 text-sm" aria-label="Breadcrumb">
      {trail.map((c, i) => {
        const last = i === trail.length - 1
        return (
          <span key={`${c.label}-${i}`} className="flex items-center gap-2">
            {last || !c.onClick ? (
              <span className={last ? 'font-medium text-theme-text-primary' : 'text-theme-text-tertiary'}>
                {c.label}
              </span>
            ) : (
              <button
                type="button"
                onClick={c.onClick}
                className="text-theme-text-tertiary hover:text-theme-text-primary hover:underline"
              >
                {c.label}
              </button>
            )}
            {!last && <span className="text-theme-text-tertiary">/</span>}
          </span>
        )
      })}
    </nav>
  )
}

/**
 * Ties a `Stepper` to the step body it drives. Spread `panelProps` on the element holding the
 * current step's fields — the tabs name it as the region they control, and without it they
 * control nothing. `useId` keeps the two ids apart when a nested wizard opens over its parent
 * and both rails are mounted at once.
 */
export function useStepper(current: number) {
  const id = useId()
  return {
    id,
    panelProps: {
      id: `${id}-panel`,
      role: 'tabpanel' as const,
      'aria-labelledby': `${id}-step-${current}`,
    },
  }
}

/**
 * Wizard progress rail, ported from the mock's `Stepper` (sap-mockup/components/ui.tsx):
 * numbered circles, a checkmark once a step is passed, a ring on the current one, and
 * connector lines between. Shared so every multi-step wizard reads the same.
 *
 * The rail is a tablist and each step a tab, so a step is reachable by pointer, by
 * Arrow/Home/End then Enter, and is announced with its position and state. Focus moves along
 * the rail without selecting; Enter or Space selects — a step change rebuilds the form under
 * it, which is too much to do on every arrow keypress.
 *
 * Which steps may be jumped to:
 *   canGoTo === undefined  -> every step reachable (EDIT: the thing already exists, so no
 *                             step is "not yet done" and a linear walk is pure friction)
 *   canGoTo(i)             -> caller decides (CREATE: only steps already visited, or a
 *                             Review built from defaults the user never saw would look chosen)
 */
export function Stepper({ id, steps, current, onGoTo, canGoTo, ariaLabel = 'Steps' }: {
  id: string
  steps: { key: string; label: string }[]
  current: number
  onGoTo: (index: number) => void
  canGoTo?: (index: number) => boolean
  ariaLabel?: string
}) {
  const tabs = useRef<(HTMLButtonElement | null)[]>([])
  // Roving tabindex: one tab stop for the whole rail, arrows to move within it. Wraps at both
  // ends, as a tablist does.
  const onKeyDown = (e: React.KeyboardEvent, i: number) => {
    const last = steps.length - 1
    const to =
      e.key === 'ArrowRight' || e.key === 'ArrowDown' ? (i === last ? 0 : i + 1)
      : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? (i === 0 ? last : i - 1)
      : e.key === 'Home' ? 0
      : e.key === 'End' ? last
      : null
    if (to === null) return
    e.preventDefault()
    tabs.current[to]?.focus()
  }
  return (
    <div role="tablist" aria-label={ariaLabel} className="mb-6 flex items-center gap-2">
      {steps.map((s, i) => {
        const done = i < current
        const active = i === current
        // The current step is always "reachable": it is where the cursor already is, and dimming
        // it says the admin cannot be where they are.
        const reachable = active || (canGoTo ? canGoTo(i) : true)
        const circle = (
          <div
            className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold ${
              // `theme-accent-blue`, not `theme-accent`: tailwind.config defines theme-accent as a
              // GROUP (blue/green/red/…) with no DEFAULT, so the bare `bg-theme-accent` these
              // circles used is not a generated utility at all and rendered unstyled.
              done
                ? 'bg-theme-accent-blue text-white'
                : active
                  ? 'bg-theme-accent-blue/20 text-theme-accent-blue ring-2 ring-theme-accent-blue'
                  : 'bg-theme-tertiary text-theme-text-tertiary'
            }`}
          >
            {done ? (
              <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" clipRule="evenodd"
                  d="M16.7 5.3a1 1 0 010 1.4l-7.5 7.5a1 1 0 01-1.4 0L3.3 9.7a1 1 0 011.4-1.4l3.3 3.3 6.8-6.8a1 1 0 011.9 0z" />
              </svg>
            ) : (
              i + 1
            )}
          </div>
        )
        const label = (
          <span className={`text-[12px] font-medium ${active ? 'text-theme-text-primary' : 'text-theme-text-tertiary'}`}>
            {s.label}
          </span>
        )
        return (
          // The wrapper only carries the connector's layout; a tablist owns tabs, nothing else.
          <div key={s.key} role="presentation" className="flex flex-1 items-center gap-2">
            <button
              type="button"
              role="tab"
              id={`${id}-step-${i}`}
              ref={(el) => { tabs.current[i] = el }}
              aria-controls={`${id}-panel`}
              aria-selected={active}
              // aria-disabled, not `disabled`: an unfinished step is still worth announcing, and
              // hiding it from the rail would leave the admin unable to tell how far the wizard runs.
              aria-disabled={reachable ? undefined : true}
              tabIndex={active ? 0 : -1}
              onClick={() => reachable && onGoTo(i)}
              onKeyDown={(e) => onKeyDown(e, i)}
              title={reachable ? `Go to step ${i + 1}: ${s.label}` : 'Complete the earlier steps first'}
              className={`flex items-center gap-2 rounded ${
                reachable ? 'hover:opacity-80' : 'cursor-not-allowed opacity-60'
              }`}
            >
              {circle}
              {label}
              {done && <span className="sr-only">completed</span>}
            </button>
            {i < steps.length - 1 && (
              <div aria-hidden="true" className="h-px flex-1 bg-theme-border-primary" />
            )}
          </div>
        )
      })}
    </div>
  )
}

// ---------------------------------------------------------------- form atoms
//
// Ported from the mock's ui.tsx with its API intact. Before these existed, every wizard
// hand-rolled its own `Field` and repeated the same input class string, which is exactly the
// drift this file was created to stop.

/** The mock's `.input`, as one string. EXPORTED because a wizard that spells its own is how two
 *  screens that ask the same question end up looking like different products — the user-experience
 *  and branding wizards had drifted onto different field backgrounds that way. */
/**
 * The reference writes every control against its `.input` class (index.css), so this is that class
 * rather than a utility string approximating it. The approximation drifted: it bordered at
 * `--c-line` where the reference borders one step brighter at `--c-line2`, which is what makes a
 * field read as a field rather than as part of the card behind it.
 */
export const inputCls = 'input'

/** The two-column split every step carrying a live preview uses: the form, then the dialog it
 *  produces. Shared so the preview sits in the same place whichever wizard raised it. */
export const previewGrid = 'grid gap-6 md:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)]'

/** `compact` drops the bottom margin — for fields laid out in a gap-ed grid. */
export function Field({ label, children, hint, compact }: {
  label: string
  children: ReactNode
  hint?: string
  compact?: boolean
}) {
  return (
    <div className={compact ? '' : 'mb-4'}>
      <label className="label">{label}</label>
      {children}
      {hint && <p className="mt-1 text-xs text-theme-text-tertiary">{hint}</p>}
    </div>
  )
}

/** Search box with the magnifier inside it and a clear affordance once it has a value —
 *  the mock's `SearchInput`. Every list page had its own copy of this markup. */
export function SearchInput({ value, onChange, placeholder = 'Search…', className = '' }: {
  value: string
  onChange: (v: string) => void
  placeholder?: string
  className?: string
}) {
  return (
    <div className={`relative ${className}`}>
      <svg
        className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ffaint"
        fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true"
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11a6 6 0 11-12 0 6 6 0 0112 0z" />
      </svg>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`${inputCls} pl-9 pr-8`}
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange('')}
          aria-label="Clear search"
          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-ffaint hover:text-ftext"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      )}
    </div>
  )
}

export function Select({ value, onChange, options, className = '' }: {
  value: string
  onChange: (v: string) => void
  options: { value: string; label: string }[]
  className?: string
}) {
  return (
    <div className={`relative ${className}`}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`${inputCls} appearance-none pr-9`}
      >
        {options.map((o) => (
          // The option list is drawn by the OS, which does not inherit the card's colours — the
          // reference paints each option so the open menu is not white-on-white in dark mode.
          <option key={o.value} value={o.value} className="bg-panel2 text-ftext">
            {o.label}
          </option>
        ))}
      </select>
      <svg
        className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ffaint"
        fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true"
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" />
      </svg>
    </div>
  )
}

/** Tone names match the mock's so its markup ports unchanged. Kept to the tones actually used;
 *  an unknown tone falls back to `slate` rather than rendering an unstyled chip. */
export type BadgeTone = 'slate' | 'green' | 'amber' | 'red' | 'blue' | 'violet'
/**
 * The reference's tones (its ui.tsx:8): a 500/15 fill under a 300 text, ringed at 500/25. The 300
 * step is unreadable on a light card, so light takes 600 and dark takes the reference's value.
 *
 * `slate` is the one that cannot be taken literally: the reference fills it with white at 5%,
 * which is invisible on our light theme's white card. `panel3` is the same step in dark and a
 * real surface in light.
 */
const BADGE_TONES: Record<BadgeTone, string> = {
  slate: 'bg-panel3 text-fmuted ring-1 ring-line2',
  green: 'bg-emerald-500/15 text-emerald-600 ring-1 ring-emerald-500/25 dark:text-emerald-300',
  amber: 'bg-amber-500/15 text-amber-600 ring-1 ring-amber-500/25 dark:text-amber-300',
  red: 'bg-red-500/15 text-red-600 ring-1 ring-red-500/25 dark:text-red-300',
  blue: 'bg-blue-500/15 text-blue-600 ring-1 ring-blue-500/25 dark:text-blue-300',
  // The reference's tone for a Private app (its ui.tsx:14) — distinct from blue on purpose, so
  // "private" never reads as an accent/primary state.
  violet: 'bg-violet-500/15 text-violet-600 ring-1 ring-violet-500/25 dark:text-violet-300',
}
/** First letters of the first two words — `Maxime Guillemin` reads as `MG`. */
export function initials(name: string): string {
  return name.split(' ').map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase()
}

/** A stable colour per name, so the same person keeps the same circle everywhere in the product. */
export function avatarColor(name: string): string {
  const palette = [
    '#2f6fed', '#e2231a', '#0ea5e9', '#8b5cf6', '#059669',
    '#d97706', '#db2777', '#0d9488', '#4f46e5', '#ca8a04',
  ]
  let hash = 0
  for (let i = 0; i < name.length; i++) hash = (31 * hash + name.charCodeAt(i)) >>> 0
  return palette[hash % palette.length]
}

/** The mock's `Avatar`: a photo when there is one, otherwise initials on a name-derived colour. */
export function Avatar({ name, size = 34, src }: { name: string; size?: number; src?: string | null }) {
  if (src) {
    return (
      <img
        src={src}
        alt={name}
        className="shrink-0 rounded-full object-cover"
        style={{ width: size, height: size }}
      />
    )
  }
  return (
    <span
      aria-hidden="true"
      className="inline-flex shrink-0 items-center justify-center rounded-full font-semibold text-white"
      style={{
        width: size,
        height: size,
        background: avatarColor(name),
        fontSize: size * 0.4,
      }}
    >
      {initials(name)}
    </span>
  )
}

export function Badge({ tone = 'slate', children }: { tone?: BadgeTone; children: ReactNode }) {
  // `.badge` is the reference's own class (index.css): a rounded-md chip at 11px semibold. A
  // rounded-full pill reads as a different component beside the reference's.
  return <span className={`badge ${BADGE_TONES[tone] ?? BADGE_TONES.slate}`}>{children}</span>
}

/**
 * Centred modal. Two widths, named as the mock names them (`wide` | `xl`).
 *
 * Note this is the OPPOSITE call from the template wizards, which were deliberately taken OUT of
 * modal chrome: those are a settings PAGE you navigate to, this is an ACTION taken against a
 * selection on the page behind it, which is what a modal is for.
 */
export function Modal({ open, onClose, title, children, footer, size = 'wide', scrollKey }: {
  open: boolean
  onClose: () => void
  title: string
  children: ReactNode
  footer?: ReactNode
  /** `full` is ours, not the mock's: a modal hosting a whole wizard with a side-by-side live
   *  preview needs more than the mock's widest, which was sized for forms. */
  size?: 'wide' | 'xl' | 'full'
  /** Change this to send the body back to the top — a wizard step is a new page, and without it
   *  you arrive at step 4 already scrolled to wherever you left step 3. */
  scrollKey?: string | number
}) {
  const body = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    if (open) document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  useEffect(() => {
    body.current?.scrollTo({ top: 0 })
  }, [scrollKey])

  if (!open) return null
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4 pt-[8vh]">
      <div className="absolute inset-0" onClick={onClose} />
      <div
        className={`relative z-10 w-full ${
          size === 'full' ? 'max-w-6xl' : size === 'xl' ? 'max-w-4xl' : 'max-w-3xl'
        } rounded-xl border border-theme-border-primary bg-theme-secondary shadow-xl`}
      >
        <div className="flex items-center justify-between border-b border-theme-border-primary px-5 py-3.5">
          <h3 className="text-base font-semibold text-theme-text-primary">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-theme-text-tertiary hover:text-theme-text-primary"
          >
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6">
              <path d="M5 5l10 10M15 5L5 15" strokeLinecap="round" />
            </svg>
          </button>
        </div>
        {/* Tall enough that a multi-step wizard does not slice a row in half against the footer,
            which reads as a rendering fault rather than "there is more below". */}
        <div ref={body} className="max-h-[74vh] overflow-y-auto px-5 py-4">
          {children}
        </div>
        {footer && (
          <div className="flex items-center justify-end gap-2 border-t border-theme-border-primary px-5 py-3.5">
            {footer}
          </div>
        )}
      </div>
    </div>
  )
}

/** Icon component a card slot renders itself, so the card owns the size. Heroicons and the mock's
 *  lucide icons both satisfy this. */
export type IconComponent = ComponentType<{ className?: string }>

/**
 * Tones are the mock's (blocks.tsx:7): a 500/15 tile under a 300 number.
 *
 * The 300 step is what the mock renders and what our dark theme — the default — must match. It is
 * far too pale on a white card, so light takes the 600 step and dark takes the mock's exact value.
 */
const STAT_TONES: Record<BadgeTone, { tile: string; value: string }> = {
  blue: { tile: 'bg-blue-500/15 text-blue-600 dark:text-blue-300', value: 'text-blue-600 dark:text-blue-300' },
  green: { tile: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-300', value: 'text-emerald-600 dark:text-emerald-300' },
  amber: { tile: 'bg-amber-500/15 text-amber-600 dark:text-amber-300', value: 'text-amber-600 dark:text-amber-300' },
  red: { tile: 'bg-red-500/15 text-red-600 dark:text-red-300', value: 'text-red-600 dark:text-red-300' },
  violet: { tile: 'bg-violet-500/15 text-violet-600 dark:text-violet-300', value: 'text-violet-600 dark:text-violet-300' },
  slate: { tile: 'bg-panel3 text-fmuted', value: 'text-ftext' },
}

/**
 * Headline number over its label, with the icon in a tinted tile — the mock's `StatCard`
 * (sap-mockup/components/blocks.tsx:7). Every page that shows a row of counts had rebuilt this,
 * each with its own tile size, value weight and label order.
 */
export function StatCard({ label, value, icon: Icon, tone = 'slate', sub }: {
  label: string
  value: ReactNode
  icon: IconComponent
  tone?: BadgeTone
  sub?: ReactNode
}) {
  const t = STAT_TONES[tone] ?? STAT_TONES.slate
  return (
    <div className="card card-pad flex items-center gap-4">
      <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${t.tile}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <div className={`text-2xl font-bold leading-none tracking-tight ${t.value}`}>{value}</div>
        <div className="mt-1.5 truncate text-xs font-medium text-fmuted">{label}</div>
        {sub && <div className="mt-0.5 text-[11px] text-ffaint">{sub}</div>}
      </div>
    </div>
  )
}

/**
 * A table in a card, with its filters in an attached toolbar and its count/paging in an attached
 * footer — the mock's `TableCard` (sap-mockup/components/blocks.tsx:51). Keeping the controls
 * inside the card's border is what makes them read as belonging to the table.
 *
 * `children` is the `<thead>`/`<tbody>`; the card owns the `<table>`.
 */
export function TableCard({ toolbar, children, footer }: {
  toolbar?: ReactNode
  children: ReactNode
  footer?: ReactNode
}) {
  return (
    <div className="overflow-hidden rounded-xl border border-theme-border-primary bg-theme-card">
      {toolbar && (
        <div className="flex flex-wrap items-center gap-2 border-b border-theme-border-primary px-4 py-3">
          {toolbar}
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">{children}</table>
      </div>
      {footer && (
        <div className="flex items-center justify-between border-t border-theme-border-primary px-4 py-2.5 text-xs text-theme-text-secondary">
          {footer}
        </div>
      )}
    </div>
  )
}

/** Placeholder rows while a table loads, as the mock's `TableSkeleton`. Loading in the table body
 *  keeps the header, toolbar and footer in place, so the page does not jump when data lands. */
export function TableSkeleton({ cols, rows = 8 }: { cols: number; rows?: number }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, r) => (
        <tr key={r} className="border-t border-theme-border-primary">
          {Array.from({ length: cols }).map((__, c) => (
            <td key={c} className="px-4 py-3">
              <div className="h-3 w-full max-w-[120px] animate-pulse rounded bg-theme-tertiary" />
            </td>
          ))}
        </tr>
      ))}
    </>
  )
}

/**
 * Titled content card — the mock's `SectionCard` (sap-mockup/components/blocks.tsx:64), which is
 * how every section on its detail pages is framed.
 */
export function SectionCard({
  title,
  action,
  children,
  className = '',
  collapsible = false,
  defaultOpen = true,
  summary,
}: {
  title: string
  action?: ReactNode
  children: ReactNode
  className?: string
  /** Fold the body away behind the title, as the mock's does. */
  collapsible?: boolean
  defaultOpen?: boolean
  /** Shown beside the title while folded, so collapsing costs no information. */
  summary?: ReactNode
}) {
  const [open, setOpen] = useState(defaultOpen)
  const folded = collapsible && !open
  return (
    <section
      className={`rounded-xl border border-theme-border-primary bg-theme-secondary ${className}`}
    >
      <div className="flex items-center justify-between gap-3 border-b border-theme-border-primary px-5 py-3">
        {collapsible ? (
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            className="flex min-w-0 items-center gap-2 text-left"
          >
            <svg
              className={`h-3.5 w-3.5 shrink-0 text-theme-text-tertiary transition-transform ${open ? 'rotate-180' : ''}`}
              viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"
            >
              <path fillRule="evenodd" clipRule="evenodd"
                d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" />
            </svg>
            <h2 className="truncate text-sm font-semibold text-theme-text-primary">{title}</h2>
            {folded && summary && (
              <span className="truncate text-[12px] text-theme-text-tertiary">{summary}</span>
            )}
          </button>
        ) : (
          <h2 className="text-sm font-semibold text-theme-text-primary">{title}</h2>
        )}
        {action && <div className="shrink-0">{action}</div>}
      </div>
      {!folded && <div className="px-5 py-4">{children}</div>}
    </section>
  )
}

/** Nothing-here state with a way out of it. The mock's `EmptyState`. */
/** The reference's `Spinner` — a ring at 20% under an arc at 80%, not a rotating arrow glyph. */
export function Spinner({ className = '' }: { className?: string }) {
  return (
    <svg className={`animate-spin ${className}`} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle className="opacity-20" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-80" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.4 0 0 5.4 0 12h4z" />
    </svg>
  )
}

/** Freshness of a slow read — whether a refresh is in flight, and how old what you are looking at is.
 *  Cached rows stay on screen while the refresh runs, so without this the page looks current when
 *  it is not. */
export function SyncStatus({ syncing, updatedAt, label, className = '' }: {
  syncing: boolean
  /** Epoch ms of the last successful response; 0 before anything has loaded. */
  updatedAt: number
  /** What is being read, e.g. `Syncing with Microsoft Intune`. */
  label: string
  className?: string
}) {
  const age = updatedAt ? relTime(new Date(updatedAt).toISOString()) : null
  if (!syncing && !age) return null
  return (
    <div className={`flex items-center gap-2 text-xs text-theme-text-tertiary ${className}`} role="status">
      {syncing && <Spinner className="h-3.5 w-3.5 shrink-0 text-theme-accent-blue" />}
      <span>
        {syncing ? label : `Updated ${age}`}
        {syncing && age && ` · showing data from ${age}`}
      </span>
    </div>
  )
}

export function EmptyState({ icon, title, hint, action }: {
  /** A glyph for the kind of thing that is missing, as the mock's (its ui.tsx:259). An empty list
   *  otherwise reads as a rendering failure rather than a state. */
  icon?: ReactNode
  title: string
  hint?: string
  action?: ReactNode
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
      {icon && (
        // The reference sets the glyph in a tinted tile rather than letting it float, which is
        // what stops an empty list reading as a half-rendered one.
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-panel3 text-ffaint">
          {icon}
        </div>
      )}
      <p className="text-base font-semibold text-ftext">{title}</p>
      {hint && <p className="max-w-md text-sm text-fmuted">{hint}</p>}
      {action}
    </div>
  )
}

/**
 * Confirmation before something irreversible, as the mock's (sap-mockup/components/ui.tsx:145).
 *
 * `message` says what is lost, not just "are you sure" — a prompt that carries no consequence
 * teaches people to click through it.
 *
 * The action runs and the dialog closes together, so a caller cannot leave it open over a
 * completed delete.
 */
export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = 'Delete',
  danger = true,
}: {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  title: string
  message: ReactNode
  confirmLabel?: string
  danger?: boolean
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm border border-theme-border-primary rounded-lg text-theme-text-secondary"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              onConfirm()
              onClose()
            }}
            className={`px-3 py-1.5 text-sm rounded-lg border ${
              danger
                ? 'border-red-500/40 bg-red-500/10 text-red-500 hover:bg-red-500/20'
                : 'border-theme-border-primary text-theme-text-primary hover:bg-theme-tertiary'
            }`}
          >
            {confirmLabel}
          </button>
        </>
      }
    >
      <div className="text-sm text-theme-text-secondary">{message}</div>
    </Modal>
  )
}

/**
 * Overflow menu, as the mock's (components/ui.tsx:164). Row actions split into two tiers: the one
 * or two you reach for constantly stay visible as buttons, and the rest — especially the
 * destructive one — go behind this, so a mis-click cannot delete a deployment.
 *
 * The panel is portalled to `document.body` and positioned from the trigger's rect: a row menu
 * sits inside `TableCard`'s `overflow-hidden` and its `overflow-x-auto` scroller, both of which
 * clip an absolutely positioned panel, so the last row's menu was cut off by the card footer. It
 * also flips above the trigger when the space below is too short to hold the panel.
 */
export function Menu({ trigger, children, align = 'right' }: {
  trigger: ReactNode
  children: (close: () => void) => ReactNode
  align?: 'left' | 'right'
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<{ top: number; left?: number; right?: number } | null>(null)

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node
      // The panel is no longer a descendant of the trigger, so it has to be excused by hand or
      // pressing an item would close the menu before the click reached it.
      if (ref.current?.contains(target) || panelRef.current?.contains(target)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [])

  // Placed after the panel mounts, so the flip decision uses its real height rather than a guess.
  // A layout effect, so it is never painted at the unplaced position.
  useLayoutEffect(() => {
    if (!open) {
      setPos(null)
      return
    }
    const place = () => {
      const t = ref.current?.getBoundingClientRect()
      if (!t) return
      const gap = 4
      const height = panelRef.current?.offsetHeight ?? 0
      const fitsBelow = window.innerHeight - t.bottom >= height + gap
      const top = fitsBelow || t.top < height + gap ? t.bottom + gap : t.top - height - gap
      setPos(
        align === 'right'
          ? { top, right: Math.max(8, window.innerWidth - t.right) }
          : { top, left: Math.max(8, t.left) },
      )
    }
    place()
    // Capture, so scrolling the table — or any other ancestor — keeps the panel on its trigger.
    window.addEventListener('scroll', place, true)
    window.addEventListener('resize', place)
    return () => {
      window.removeEventListener('scroll', place, true)
      window.removeEventListener('resize', place)
    }
  }, [open, align])

  return (
    <div className="relative" ref={ref}>
      <div onClick={() => setOpen((o) => !o)}>{trigger}</div>
      {open &&
        createPortal(
          <div
            ref={panelRef}
            role="menu"
            style={{ ...pos, visibility: pos ? 'visible' : 'hidden' }}
            className="fixed z-50 min-w-[190px] overflow-hidden rounded-xl border border-theme-border-primary bg-theme-secondary py-1 shadow-xl"
          >
            {children(() => setOpen(false))}
          </div>,
          document.body,
        )}
    </div>
  )
}

export function MenuItem({ children, onClick, danger }: {
  children: ReactNode
  onClick?: () => void
  danger?: boolean
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={`flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm hover:bg-theme-tertiary ${
        danger ? 'text-red-500' : 'text-theme-text-primary'
      }`}
    >
      {children}
    </button>
  )
}

/** On/off switch, as the mock's (sap-mockup/components/ui.tsx:42). */
/** A checkbox with its label and optional hint beside it. A checkbox, not a switch: these answer
 *  "is this true of the app" rather than turning a feature on, and the reference draws them the
 *  same way. */
export function Checkbox({ checked, onChange, label, hint, disabled }: {
  checked: boolean
  onChange: (v: boolean) => void
  label: string
  hint?: string
  disabled?: boolean
}) {
  return (
    <label className={`flex items-start gap-2.5 ${disabled ? 'opacity-60' : 'cursor-pointer'}`}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 shrink-0 rounded border-theme-border-primary bg-theme-primary"
      />
      <span className="min-w-0">
        <span className="block text-sm text-theme-text-primary">{label}</span>
        {hint && <span className="mt-0.5 block text-xs text-theme-text-tertiary">{hint}</span>}
      </span>
    </label>
  )
}

export function Toggle({ checked, onChange, label, disabled }: {
  checked: boolean
  onChange: (v: boolean) => void
  label?: string
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onChange(!checked)}
      aria-pressed={checked}
      aria-label={label}
      disabled={disabled}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
        checked ? 'bg-theme-accent-blue' : 'bg-theme-tertiary'
      }`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
          checked ? 'translate-x-5' : 'translate-x-0.5'
        }`}
      />
    </button>
  )
}
